import { clsx } from 'clsx';
import { useEffect, type CSSProperties, type ReactNode } from 'react';

/**
 * Frame / Main / TwoCol — the app window (deskkit.frame, deskkit.main).
 *
 * The kit's `frame()` pins 1440x1024 because an artboard is a fixed picture.
 * Law 1 says the product is ONE responsive webapp, so this is fluid instead:
 * rail + main + optional pane, with the phone as the base and the desk as the
 * override at 1024px (see kit-extras.css).
 *
 * What changes at narrow width is specified by the design, not invented here
 * (README §5): the rail goes off-canvas behind a burger, and the pane becomes a
 * full sheet because it cannot dock beside a 375px column. Everything else is
 * the same product.
 */

interface FrameProps {
  rail?: ReactNode;
  children: ReactNode;
  pane?: ReactNode;
  overlays?: ReactNode;
  /** Phone only — the rail is off-canvas until this is true. */
  railOpen?: boolean;
  onCloseRail?: () => void;
  className?: string;
}

export function Frame({
  rail,
  children,
  pane,
  overlays,
  railOpen = false,
  onCloseRail,
  className,
}: FrameProps) {
  // Escape closes the off-canvas rail. On the desk the rail is not a layer, so
  // there is nothing to close and the handler is not attached.
  useEffect(() => {
    if (!railOpen || !onCloseRail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [railOpen, onCloseRail]);

  return (
    <div className={clsx('maie', 'desk', className)}>
      <div className="dframe">
        {rail}
        {/* Phone only — kit-extras.css hides the scrim at desk width, where the
            rail is a column rather than a layer. Pass `open` to <Rail>. */}
        {rail && railOpen ? (
          <div
            className="rail-scrim"
            onClick={onCloseRail}
            role="presentation"
            aria-hidden="true"
          />
        ) : null}

        {children}

        {pane}
      </div>
      {overlays}
    </div>
  );
}

/**
 * Main — the content column: top bar, optional filter row, then content
 * (deskkit.main). A SubRail sits OUTSIDE it, as its own column.
 *
 * Filters live on their own row and never in the TopBar (§5, §10).
 */
export function Main({
  topBar,
  filters,
  subRail,
  children,
  className,
}: {
  topBar?: ReactNode;
  filters?: ReactNode;
  subRail?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <>
      {subRail}
      <div className={clsx('dmain', className)}>
        {topBar}
        {filters}
        <div className="dcontent">{children}</div>
      </div>
    </>
  );
}

/**
 * TwoCol — deskkit.two_col.
 *
 * The kit hardcodes two equal flex columns. Here they stack below the desk
 * breakpoint: two 50% columns inside a phone viewport is not a narrower desk,
 * it is an unreadable one.
 */
export function TwoCol({
  left,
  right,
  gap = 20,
  className,
}: {
  left: ReactNode;
  right: ReactNode;
  gap?: number;
  className?: string;
}) {
  return (
    <div
      className={clsx('kit-twocol', className)}
      style={{
        display: 'grid',
        gap,
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        alignItems: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>{left}</div>
      <div style={{ minWidth: 0 }}>{right}</div>
    </div>
  );
}

/**
 * FilterRow — filters on their own row (deskkit.filterrow).
 *
 * Wraps rather than scrolls: §13.13 says nothing in the main column may sit
 * under the pane, and a filter that scrolls out of reach is the same defect in
 * a different coat.
 */
export function FilterRow({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return <div className={clsx('filterrow', className)} style={style}>{children}</div>;
}
